import { SECURITY_EVENTS_TYPES, SYSLOG_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { ColorScheme, Icon, Icons, Info, Toggle } from '@silverfort/sf-components';
import serviceAccountTooltip from './images/ServiceAccountTooltip.png';
import { Tooltip } from '@material-ui/core';
import { CUSTOMIZABLE_EVENTS, EVENT_MODULES_MAP } from './AlertsOptionsConsts';
import { DisabledDisplayOptions } from '../../../hooks/lockedFeatures/types.ts';
import { FeatureLock } from '../../../../common/LockedFeatures/index.ts';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext.tsx';

const buildServiceAccountTooltip = (alertType) => {
    return (
        <div className="p-2">
            <div className="text-13 font-bold mb-2">{`Customize ${alertType} Trigger`}</div>
            <div className="text-12 font-normal leading-[18px]">
                On the Service Accounts page, select Send to SIEM next to the service accounts that trigger this{' '}
                {alertType}.
            </div>
            <img className="mt-2" alt="" src={serviceAccountTooltip} />
        </div>
    );
};

const buildEventLicenseTooltip = (eventType) => {
    const eventConfig = EVENT_MODULES_MAP[eventType];
    if (!eventConfig) return null;

    return eventConfig.tooltip;
};

const AlertsOptionsToggles = ({
    title,
    alertType = 'Alert',
    data,
    setData,
    eventInEdit,
    setEventInEdit,
    eventsTypes,
    customizedEvents = CUSTOMIZABLE_EVENTS,
    editableEvents = {},
    className = ''
}) => {
    const { pricingModulesState } = usePricingModulesState();

    const checkCloudEnabled = title === 'Cloud events';

    const isEventLicensed = (eventId) => {
        if (!pricingModulesState) return true;

        const eventConfig = EVENT_MODULES_MAP[eventId];
        if (!eventConfig) return true;

        return eventConfig.requiredModules.some((moduleName) => {
            const moduleState = pricingModulesState[moduleName];
            return checkCloudEnabled ? moduleState?.cloudEnabled : moduleState?.anyEnabled;
        });
    };

    return (
        <div className={className || 'min-h-32'}>
            <div className="flex items-center text-gray-500 text-12 leading-[18px] h-10 font-medium">{title}</div>
            <div className="grid grid-cols-3 gap-2 text-13 min-h-10 max-w-[870px]">
                {Object.keys(eventsTypes).map((type) => {
                    const customizable = customizedEvents.has(eventsTypes[type].id);
                    const isEditable = editableEvents[eventsTypes[type].id] !== false;
                    const eventId = eventsTypes[type].id;
                    const isLicensed = isEventLicensed(eventId);
                    const tooltipProps = !isLicensed ? buildEventLicenseTooltip(eventId) : null;

                    const toggleElement = (
                        <div
                            key={type}
                            className="group transition ease-out duration-300 hover:bg-sf-gray-100 px-3 rounded-lg flex h-10 justify-between min-w-0">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Toggle
                                    className="flex-shrink-0"
                                    checked={data.activeEvents?.[eventId]}
                                    disabled={!isEditable}
                                    onChange={() => {
                                        if (!isEditable) {
                                            return;
                                        }

                                        if (
                                            !data.activeEvents?.[eventId] &&
                                            eventId === SECURITY_EVENTS_TYPES.POLICY_MATCH.id
                                        ) {
                                            setEventInEdit(eventId);
                                        }

                                        setData({
                                            ...data,
                                            activeEvents: {
                                                ...data.activeEvents,
                                                [eventId]: !data.activeEvents?.[eventId]
                                            }
                                        });
                                    }}
                                />
                                <div className="flex items-center text-sf-gray-800 flex-1 min-w-0">
                                    <span className="truncate">{eventsTypes[type].label}</span>
                                    {!isLicensed && (
                                        <Icon
                                            icon={Icons.Warning}
                                            className="ml-2 flex-shrink-0 text-sf-neutral-500"
                                            size={16}
                                        />
                                    )}
                                    {eventId === SECURITY_EVENTS_TYPES.SA_ALERTS.id && (
                                        <Tooltip
                                            id="serviceAccountViolationTooltip"
                                            title={buildServiceAccountTooltip(alertType)}
                                            enterDelay={100}
                                            placement={'bottom'}
                                            classes={{ tooltip: 'bg-sf-gray-900 w-96' }}>
                                            <div className={`group-hover:flex hidden rounded-lg ml-2`}>
                                                <Info />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {eventId === SYSLOG_TYPES.INTEGRATIONS.id && (
                                        <Tooltip
                                            id="syslogSplunkTooltip"
                                            title={
                                                <div className="p-2">
                                                    <div className="text-13 font-medium">
                                                        Select this option to send the events specific to the Silverfort
                                                        Splunk app.
                                                    </div>
                                                </div>
                                            }
                                            enterDelay={100}
                                            placement={'bottom'}
                                            classes={{ tooltip: 'bg-sf-gray-900 w-96' }}>
                                            <div className={`group-hover:flex hidden rounded-lg ml-2`}>
                                                <Info />
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            {customizable && (
                                <div
                                    className="cursor-pointer hidden select-none group-hover:flex items-center text-sf-primary-500 flex-shrink-0"
                                    onClick={() => {
                                        if (eventInEdit === eventId) {
                                            setEventInEdit(null);
                                        } else {
                                            setEventInEdit(eventId);
                                        }
                                    }}>
                                    <Icon icon={Icons.Tune} fill={ColorScheme['sf-primary-500']} size={16} />
                                    <span className="ml-1">Custom</span>
                                </div>
                            )}
                        </div>
                    );

                    if (!isLicensed && tooltipProps) {
                        return (
                            <FeatureLock
                                key={type}
                                displayOptions={{
                                    enabled: false,
                                    disabledDisplay: DisabledDisplayOptions.TOOLTIP,
                                    tooltipProps
                                }}>
                                {toggleElement}
                            </FeatureLock>
                        );
                    }

                    return toggleElement;
                })}
            </div>
        </div>
    );
};

export default AlertsOptionsToggles;
